import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AUTH } from "@/constants/testIds";
import { useSession, formatApiErrorDetail } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendVerification } = useSession();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setError("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        navigate("/onboarding", { replace: true });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "This verification link is invalid or has expired.");
      }
    })();
  }, [params, verifyEmail, navigate]);

  const onResend = async () => {
    setResendMsg("");
    try {
      await resendVerification(email);
      setResendMsg("If an unverified account exists, a new link is on its way.");
    } catch (err) {
      setResendMsg(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend. Try again shortly.");
    }
  };

  return (
    <div
      data-testid={AUTH.verifyRoot}
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-background text-foreground"
    >
      <div aria-hidden="true" className="sema-grid pointer-events-none absolute inset-0 opacity-30" />
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center gap-4 px-6 text-center">
        {error ? (
          <>
            <ShieldAlert className="h-8 w-8 text-amber-400" />
            <p data-testid={AUTH.verifyError} className="text-sm text-muted-foreground">
              {error}
            </p>
            <div className="mt-2 w-full space-y-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid={AUTH.verifyResendEmail}
                className="h-11 bg-card/60"
              />
              <Button
                type="button"
                onClick={onResend}
                data-testid={AUTH.verifyResend}
                className="h-11 w-full rounded-full sema-cta-gradient font-mono text-[11px] uppercase tracking-[0.22em] text-white"
              >
                Send a new link
              </Button>
              {resendMsg && <p className="text-xs text-muted-foreground">{resendMsg}</p>}
            </div>
            <Link to="/login" className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <MailCheck className="h-8 w-8 text-accent" />
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">Verifying your email…</p>
          </>
        )}
      </div>
    </div>
  );
}
